import { isSameDay, startOfDay } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef } from "react";
import {
  DayPicker,
  useDayRender,
  useInput,
  type DayClickEventHandler,
  type DayProps,
} from "react-day-picker";
import { getUkDate } from "~/utils/date";
import { cn } from "~/utils/shadcn";

type CalendarProps = React.ComponentProps<typeof DayPicker>;
export function Calendar({
  className,
  classNames,
  fromDate,
  toDate,
  availabilities,
  // @TODO: Are these necessary
  value: date,
  onChange,
  admin = false,
  ...props
}: CalendarProps & {
  value: string;
  onChange: (date: string) => void;
  availabilities?: {
    date: Date;
    count: number;
  }[];
  admin?: boolean;
}) {
  const { dayPickerProps, setSelected } = useInput({
    ...props,
    defaultSelected: date ? getUkDate(date) : undefined,
    format: "PP",
    required: true,
  });

  let onChangeHandler: DayClickEventHandler = (day) => {
    // if (!day) return;
    onChange(startOfDay(getUkDate(day)).toString());
    setSelected(day);
  };

  useEffect(() => {
    setSelected(date ? getUkDate(date) : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const getAvailabilitiesForDate = (date: Date) => {
    return availabilities?.find((availability) =>
      isSameDay(getUkDate(availability.date), getUkDate(date))
    )?.count;
  };

  return (
    <DayPicker
      {...dayPickerProps}
      onDayClick={onChangeHandler}
      fromDate={fromDate}
      toDate={toDate}
      footer={
        <input
          name="date"
          hidden
          readOnly
          value={getUkDate(date ?? undefined).toString()}
        />
      }
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col space-y-2 sm:space-y-4",
        month: "space-y-2 sm:space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: "h-5 w-5 sm:h-7 sm:w-7 bg-transparent p-0 opacity-50",
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex w-full justify-around",
        head_cell:
          "text-gray-400 rounded-md w-7 sm:w-9 font-normal text-[0.7rem] sm:text-[0.8rem] w-full",
        row: "flex w-full mt-2",
        cell: "text-sm p-0 relative w-full",
        day: "rounded h-8 w-12 sm:h-12 sm:w-12 p-0 font-normal opacity-100 flex justify-center items-center mx-auto",
        day_selected: "bg-indigo-500/30 border border-white/30",
        day_today: "border border-white/10 border-px",
        day_disabled: "text-gray-400 opacity-50",
        day_range_middle: "opacity-100 text-gray-400",
        day_hidden: "invisible",
        ...classNames,
      }}
      disabled={(day) => {
        if (admin) return false;

        return !getAvailabilitiesForDate(day);
      }}
      components={{
        Day: (props) => (
          <Day
            {...props}
            availabilityCount={getAvailabilitiesForDate(props.date)}
          />
        ),
        IconLeft: () => <ChevronLeft className="h-4 w-4" />,
        IconRight: () => <ChevronRight className="h-4 w-4" />,
      }}
    />
  );
}

const Day = ({
  date,
  displayMonth,
  availabilityCount,
}: DayProps & {
  availabilityCount: number | undefined;
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dayRender = useDayRender(getUkDate(date), displayMonth, buttonRef);

  const dotColor = (bookingCount?: number) => {
    switch (bookingCount) {
      case undefined:
        return "#ccc"; // Grey color for undefined values, adjust if needed
      case 1:
      case 2:
        return "red"; // Low availability
      case 3:
      case 4:
      case 5:
      case 6:
        return "orange"; // Medium availability
      default:
        return "#35d884"; // High availability
    }
  };

  // When there are more than 6 bookings, we display 3 dots
  // When there are 3 to 6 bookings, we display 2 dots
  // When there are 1 to 2 bookings, we display 1 dot
  // When there are no bookings, we display no dots
  const dotCount = (availabilityCount?: number) => {
    switch (availabilityCount) {
      case undefined:
      case 0:
        // @TODO: Make the day disabled
        return 0;
      case 1:
      case 2:
        return 1;
      case 3:
      case 4:
      case 5:
      case 6:
        return 2;
      default:
        return 3;
    }
  };

  return dayRender.isButton ? (
    <button {...dayRender.buttonProps} ref={buttonRef} type="button">
      <div className="flex h-full flex-col items-center justify-center">
        <div className="h-1/3"></div>
        <div>{dayRender.buttonProps.children}</div>
        <div className="h-1/3">
          {!dayRender.activeModifiers.disabled &&
          !dayRender.activeModifiers.hidden
            ? Array.from({ length: dotCount(availabilityCount) }).map(
                (_, i) => (
                  <span
                    key={i}
                    className="text-sm [--px:-0.4rem] sm:text-2xl sm:[--px:-0.75rem]"
                    style={{
                      color: dotColor(availabilityCount),
                      fontWeight: "bold",
                      position: "relative",
                      top: "var(--px)",
                    }}
                  >
                    •
                  </span>
                )
              )
            : null}
        </div>
      </div>
    </button>
  ) : null;
};
